import React, { useCallback, useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { Button } from "reactstrap";
import notifications from "../../common/Components/Notifications/Notification";
import { canManageKarkhana } from "../../helpers/karkhanaEnabled";
import { discardHistoryImport, historyImportRows, keepHistoryImport, listHistoryImports, uploadHistoryImport } from "../../services/karkhanaApi";
import "../gi-registry/gi-registry.scss";
import { fmtDay } from "./rates/rateModel";
import "./workers/karkhana-workers.scss";
import "./rounds/karkhana-rounds.scss";
import PageHeader from "./ui/PageHeader";
import { confirmDialog } from "./ui/dialog";
import { kp } from "./karkhanaPaths";

const readRole = () => {
  try {
    return JSON.parse(localStorage.getItem("authUser") || "null")?.users?.role;
  } catch {
    return undefined;
  }
};

const ISSUES: Record<string, string> = {
  BAD_DATE: "Date can't be read (e.g. year 0226)",
  MISSING_WORKER: "No worker code (#N/A, #REF!)",
  UNKNOWN_WORKER: "Worker code not in HKP",
  UNKNOWN_HELPER: "Helper code not in HKP",
  UNKNOWN_WORK_TYPE: "Work type not known",
  NO_INCHES: "No inches / 0 inches",
  BAD_INCHES: "Inches look wrong",
  NO_LOOM: "No loom",
  DUPLICATE_IN_FILE: "Repeated row (left out)",
  ALREADY_IMPORTED: "Already imported (left out)",
};

/**
 * The old Google Sheet's DAILY WORK tab as read-only history (K12): upload, read the cleanup report,
 * then keep it. History is never paid; it shows on products as production history.
 */
const KarkhanaHistoryImportPage = () => {
  const history = useHistory();
  const role = readRole();
  const fileRef = useRef<HTMLInputElement>(null);
  const [batches, setBatches] = useState<any[]>([]);
  const [batch, setBatch] = useState<any>(null);
  const [issue, setIssue] = useState<string>("");
  const [rows, setRows] = useState<any>(null);
  const [busy, setBusy] = useState("");

  const loadBatches = useCallback(async () => {
    const res: any = await listHistoryImports().catch(() => null);
    setBatches(res?.success ? res.data || [] : []);
  }, []);

  useEffect(() => {
    if (!canManageKarkhana(role)) {
      history.replace(kp.home());
      return;
    }
    loadBatches();
  }, [role, history, loadBatches]);

  useEffect(() => {
    if (!batch?.id) return;
    historyImportRows(batch.id, issue || undefined)
      .then((res: any) => setRows(res?.success ? res.data : null))
      .catch(() => setRows(null));
  }, [batch, issue]);

  const upload = async (f?: File | null) => {
    if (!f) return;
    setBusy("upload");
    setBatch(null);
    setRows(null);
    try {
      const res: any = await uploadHistoryImport(f);
      if (!res?.success) {
        notifications({ title: "Error", message: res?.message || "Could not read the file", type: "danger" });
        return;
      }
      setBatch(res.data);
      setIssue("");
      loadBatches();
    } catch {
      notifications({ title: "Error", message: "Could not upload the file (it may be too large or the signal dropped)", type: "danger" });
    } finally {
      setBusy("");
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const keep = async () => {
    if (!(await confirmDialog({ title: `Keep ${batch.rows - (batch.issueCounts?.DUPLICATE_IN_FILE || 0) - (batch.issueCounts?.ALREADY_IMPORTED || 0)} rows as history? They are never paid.` }))) return;
    setBusy("keep");
    const res: any = await keepHistoryImport(batch.id).catch(() => null);
    setBusy("");
    if (!res?.success) {
      notifications({ title: "Error", message: res?.message || "Could not keep it", type: "danger" });
      return;
    }
    setBatch(res.data);
    loadBatches();
    notifications({ title: "Kept", message: "The old sheet is now history. It shows on products by saree number.", type: "success" });
  };

  const discard = async () => {
    if (!(await confirmDialog({ title: "Discard this check? Nothing is kept." }))) return;
    setBusy("discard");
    const res: any = await discardHistoryImport(batch.id).catch(() => null);
    setBusy("");
    if (!res?.success) {
      notifications({ title: "Error", message: res?.message || "Could not discard", type: "danger" });
      return;
    }
    setBatch(null);
    setRows(null);
    loadBatches();
  };

  if (!canManageKarkhana(role)) return null;
  const counts: Record<string, number> = batch?.issueCounts || {};

  return (
    <div className="gi-registry-page kw-page">
      <PageHeader title="Import the old sheet" back={kp.settings()} />
      <p className="kw-lede">
        Optional. In Google Sheets open the <strong>DAILY WORK</strong> tab, then File → Download → CSV, and upload it here. HKP checks every row
        and shows what needs cleaning. Kept rows become read-only history: they are <strong>never paid</strong>, they show on each product's
        production history by saree number, and old saree numbers are never reused.
      </p>
      <div className="kw-toolbar">
        <Button className="ResetBtn" disabled={!!busy} onClick={() => fileRef.current?.click()}>
          {busy === "upload" ? "Checking… (large files take a minute)" : "Choose the CSV"}
        </Button>
        <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls,text/csv" hidden onChange={(e) => upload(e.target.files?.[0])} />
      </div>

      {batch ? (
        <div className="kw-card">
          <h3>
            {batch.fileName} · {batch.rows.toLocaleString("en-IN")} rows
          </h3>
          <p className="kw-lede">
            {batch.firstDate ? `${fmtDay(batch.firstDate)} – ${fmtDay(batch.lastDate)} · ` : ""}
            {batch.sarees.toLocaleString("en-IN")} sarees · {batch.ok.toLocaleString("en-IN")} clean rows · {batch.problems.toLocaleString("en-IN")} with something to note
            {batch.status === "KEPT" ? " · kept as history" : ""}
          </p>
          <div className="kr-chips" style={{ marginBottom: 10 }}>
            <button type="button" className={`kr-choice${issue === "" ? " is-on" : ""}`} onClick={() => setIssue("")}>
              All flagged
            </button>
            {Object.entries(counts).map(([k, n]) => (
              <button key={k} type="button" className={`kr-choice${issue === k ? " is-on" : ""}`} onClick={() => setIssue(k)}>
                {ISSUES[k] || k} · {n}
              </button>
            ))}
          </div>
          {rows ? (
            <>
              <div className="table-responsive">
                <table className="table table-sm">
                  <thead>
                    <tr>
                      <th>Row</th>
                      <th>Date</th>
                      <th>Loom</th>
                      <th>Saree</th>
                      <th>Worker</th>
                      <th>Helper</th>
                      <th>Work</th>
                      <th className="text-end">Inches</th>
                      <th>Note</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(rows.rows || []).map((r: any) => (
                      <tr key={r.rowNo}>
                        <td>{r.rowNo}</td>
                        <td>{r.rawDate}</td>
                        <td>{r.loomCode}</td>
                        <td>{r.sareeNumber}</td>
                        <td>{r.workerCode}</td>
                        <td>{r.helperCode}</td>
                        <td>{r.workTypeCode}</td>
                        <td className="text-end">{r.inches}</td>
                        <td className="kw-muted">{(r.issues || []).map((i: string) => ISSUES[i] || i).join(", ")}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {rows.total > 50 ? <p className="kw-muted">Showing 50 of {rows.total.toLocaleString("en-IN")}. Fix these in the sheet and upload again if you want them clean.</p> : null}
            </>
          ) : null}
          {batch.status !== "KEPT" ? (
            <div className="kw-toolbar">
              <Button className="ResetBtn" disabled={!!busy} onClick={keep}>
                {busy === "keep" ? "Keeping…" : "Keep as history"}
              </Button>
              <Button outline disabled={!!busy} onClick={discard}>
                Discard
              </Button>
            </div>
          ) : null}
        </div>
      ) : null}

      {batches.length ? (
        <>
          <h3 className="kr-group-title">Earlier uploads</h3>
          {batches.map((b) => (
            <button key={b.id} type="button" className="kr-loom" onClick={() => { setBatch(b); setIssue(""); }}>
              <span className="kr-loom__code" style={{ fontSize: 13 }}>
                {b.status === "KEPT" ? "Kept" : "Checked"}
              </span>
              <span className="kr-loom__main">
                <span className="kr-loom__line">{b.fileName}</span>
                <span className="kr-loom__line kw-muted">
                  {b.rows.toLocaleString("en-IN")} rows · {b.uploadedByName}
                </span>
              </span>
            </button>
          ))}
        </>
      ) : null}
    </div>
  );
};

export default KarkhanaHistoryImportPage;
